import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const STORAGE_KEY = "infocity_cookie_consent";

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function applyConsent(consent) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, decided_at: new Date().toISOString() }));
  // PostHog's own opt-in/opt-out API — the correct, library-supported way to
  // start/stop capturing without us hand-rolling cookie cleanup.
  if (window.posthog) {
    if (consent.analytics) window.posthog.opt_in_capturing();
    else window.posthog.opt_out_capturing();
  }
}

// Global banner + reusable "preferences" entry point. Footer/Privacy settings
// dispatch a window "open-cookie-preferences" event to reopen this at any time.
export function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analyticsChoice, setAnalyticsChoice] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setVisible(true);
    else setAnalyticsChoice(!!existing.analytics);

    const reopen = () => {
      const current = getCookieConsent();
      setAnalyticsChoice(current ? !!current.analytics : false);
      setVisible(true);
      setShowPrefs(true);
    };
    window.addEventListener("open-cookie-preferences", reopen);
    return () => window.removeEventListener("open-cookie-preferences", reopen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => { applyConsent({ necessary: true, analytics: true }); setVisible(false); setShowPrefs(false); };
  const rejectAll = () => { applyConsent({ necessary: true, analytics: false }); setVisible(false); setShowPrefs(false); };
  const savePrefs = () => { applyConsent({ necessary: true, analytics: analyticsChoice }); setVisible(false); setShowPrefs(false); };

  return (
    <div data-testid="cookie-consent-banner" className="fixed bottom-0 left-0 right-0 z-[70] p-3.5 sm:p-5 bg-white border-t-2 border-slate-200 rounded-t-2xl shadow-[0_-10px_36px_rgba(15,23,42,0.16)]" style={{ paddingBottom: "calc(14px + env(safe-area-inset-bottom))" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-3">
          <Cookie className="h-5 w-5 text-[#028CF8] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 leading-relaxed">
              {t("cookies.bannerText")}{" "}
              <Link to="/privacy" className="text-[#028CF8] font-medium hover:underline">{t("cookies.learnMore")}</Link>
            </p>

            {showPrefs && (
              <div className="mt-2.5 space-y-1.5 border-t border-slate-100 pt-2.5">
                <label className="flex items-center justify-between gap-3 text-sm text-slate-600">
                  <span>{t("cookies.necessary")}</span>
                  <input type="checkbox" checked disabled className="h-4 w-4" data-testid="cookie-necessary-toggle" />
                </label>
                <label className="flex items-center justify-between gap-3 text-sm text-slate-600">
                  <span>{t("cookies.analytics")}</span>
                  <input type="checkbox" checked={analyticsChoice} onChange={(e) => setAnalyticsChoice(e.target.checked)}
                    className="h-4 w-4 accent-[#028CF8]" data-testid="cookie-analytics-toggle" />
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2.5 sm:mt-3">
              {!showPrefs && (
                <button onClick={() => setShowPrefs(true)} data-testid="cookie-preferences-button"
                  className="h-9 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                  {t("cookies.preferences")}
                </button>
              )}
              {showPrefs && (
                <button onClick={savePrefs} data-testid="cookie-save-preferences-button"
                  className="h-9 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold bg-[#028CF8] text-white hover:bg-[#0270C5] transition-colors">
                  {t("cookies.savePreferences")}
                </button>
              )}
              <button onClick={rejectAll} data-testid="cookie-reject-button"
                className="h-9 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                {t("cookies.reject")}
              </button>
              <button onClick={acceptAll} data-testid="cookie-accept-button"
                className="h-9 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-semibold bg-[#028CF8] text-white hover:bg-[#0270C5] transition-colors">
                {t("cookies.accept")}
              </button>
            </div>
          </div>
          <button onClick={rejectAll} aria-label="close" data-testid="cookie-banner-dismiss" className="text-slate-400 hover:text-slate-600 flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
